import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { RefreshCw, MapPin, Building2, Store, AlertCircle, Users } from 'lucide-react'
import { toast } from 'sonner'
import { locationService, LocationStats, Region, District, Director, Store as StoreType } from '@/services/locationService'

const UserLocationDisplay: React.FC = () => {
  const [stats, setStats] = useState<LocationStats | null>(null)
  const [regions, setRegions] = useState<Region[]>([])
  const [districts, setDistricts] = useState<District[]>([])
  const [directors, setDirectors] = useState<Director[]>([])
  const [stores, setStores] = useState<StoreType[]>([])
  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchInitialData()
  }, [])

  const fetchInitialData = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const [statsData, regionsData, districtsData, directorsData, storesData] = await Promise.all([
        locationService.getLocationStats(),
        locationService.getRegions(),
        locationService.getDistricts(),
        locationService.getDirectors(),
        locationService.getStores() // Get retail stores only (backend filters automatically)
      ])
      
      setStats(statsData)
      setRegions(regionsData)
      setDistricts(districtsData)
      setDirectors(directorsData)
      setStores(storesData)
    } catch (err) {
      console.error('Error fetching location data:', err)
      setError('Failed to load location data')
      toast.error('Failed to load location data')
    } finally {
      setLoading(false)
    }
  }

  const handleSyncPowerBI = async () => {
    try {
      setSyncing(true)
      console.log('🚀 Starting PowerBI sync...')
      
      const result = await locationService.syncFromPowerBI()
      
      if (result.success) {
        toast.success(result.message)
        console.log('✅ PowerBI sync successful:', result)
        
        // Refresh all data after successful sync
        await fetchInitialData()
      } else {
        toast.error(result.message)
        console.error('❌ PowerBI sync failed:', result.errors)
      }
    } catch (err) {
      console.error('PowerBI sync error:', err)
      toast.error('PowerBI sync failed. Please try again.')
    } finally {
      setSyncing(false)
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium">Locations</h3>
            <p className="text-muted-foreground">Loading location data...</p>
          </div>
        </div>
        <div className="animate-pulse space-y-4">
          <div className="h-32 bg-muted rounded-lg"></div>
          <div className="h-64 bg-muted rounded-lg"></div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium">Locations</h3>
            <p className="text-muted-foreground">Manage location data and sync from PowerBI</p>
          </div>
        </div>
        <div className="flex items-center gap-2 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
          <AlertCircle className="h-5 w-5 text-destructive" />
          <span className="text-destructive">{error}</span>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium">Locations</h3>
          <p className="text-muted-foreground">
            Manage location data and sync from PowerBI
          </p>
        </div>
        
        <Button 
          onClick={handleSyncPowerBI}
          disabled={syncing}
          className="flex items-center gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
          {syncing ? 'Syncing...' : 'Sync PowerBI'}
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Regions</CardTitle>
              <MapPin className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalRegions}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Districts</CardTitle>
              <Building2 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalDistricts}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Directors</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalDirectors}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Stores</CardTitle>
              <Store className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalStores}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Location Data Tabs */}
      <Card>
        <CardHeader>
          <CardTitle>Location Data</CardTitle>
          <CardDescription>
            Browse regions, districts, directors, and stores
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="regions" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="regions">Regions</TabsTrigger>
              <TabsTrigger value="districts">Districts</TabsTrigger>
              <TabsTrigger value="directors">Directors</TabsTrigger>
              <TabsTrigger value="stores">Stores</TabsTrigger>
            </TabsList>
            
            <TabsContent value="regions" className="space-y-4 mt-4">
              <div className="space-y-2">
                {regions.map((region) => (
                  <div key={region.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <MapPin className="h-4 w-4 text-blue-600" />
                      <div>
                        <div className="font-medium">{region.name}</div>
                        <div className="text-sm text-muted-foreground">ID: {region.regionId}</div>
                      </div>
                    </div>
                  </div>
                ))}
                {regions.length === 0 && (
                  <div className="text-center py-6 text-muted-foreground">
                    No regions found
                  </div>
                )}
              </div>
            </TabsContent>
            
            <TabsContent value="districts" className="space-y-4 mt-4">
              <div className="space-y-2">
                {districts.map((district) => (
                  <div key={district.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <Building2 className="h-4 w-4 text-green-600" />
                      <div>
                        <div className="font-medium">{district.name}</div>
                        <div className="text-sm text-muted-foreground">
                          ID: {district.districtId} • Region: {district.region?.name || district.regionId}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                {districts.length === 0 && (
                  <div className="text-center py-6 text-muted-foreground">
                    No districts found
                  </div>
                )}
              </div>
            </TabsContent>
            
            <TabsContent value="directors" className="space-y-4 mt-4">
              <div className="space-y-2">
                {directors.map((director) => (
                  <div key={director.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/30">
                    <div className="flex items-center gap-3">
                      <Users className="h-4 w-4 text-purple-600" />
                      <div>
                        <div className="font-medium text-base">{director.name}</div>
                        {director.district && (
                          <div className="text-sm text-blue-600">
                            {director.district.name}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-muted-foreground">Director #{director.directorId}</div>
                      {director.district?.region && (
                        <div className="text-sm text-muted-foreground">
                          {director.district.region.name}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {directors.length === 0 && (
                  <div className="text-center py-6 text-muted-foreground">
                    No directors found
                  </div>
                )}
              </div>
            </TabsContent>
            
            <TabsContent value="stores" className="space-y-4 mt-4">
              <div className="space-y-2">
                {stores.map((store) => (
                  <div key={store.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/20">
                    <div className="flex items-center gap-3">
                      <Store className="h-4 w-4 text-orange-600" />
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{store.name}</span>
                          {store.compStatus && (
                            <Badge 
                              variant={store.compStatus === 'Comp' ? 'outline' : 'destructive'}
                              className="text-xs"
                            >
                              {store.compStatus}
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          ID: {store.storeId}{store.abbreviation && store.abbreviation !== store.storeId ? ` • ${store.abbreviation}` : ''}
                        </div>
                        {store.originalOpenDate && (
                          <div className="text-xs text-muted-foreground">
                            Opened: {new Date(store.originalOpenDate).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="text-right text-sm">
                      <div>Region: {store.region?.name || store.regionId}</div>
                      <div className="text-muted-foreground">District: {store.district?.name || store.districtId}</div>
                      {store.managerEmail && (
                        <div className="text-blue-600">{store.managerEmail}</div>
                      )}
                      {store.storeAge && (
                        <div className="text-xs text-muted-foreground mt-1">
                          {store.storeAge}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {stores.length === 0 && (
                  <div className="text-center py-6 text-muted-foreground">
                    No retail stores found
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  )
}

export default UserLocationDisplay